import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { computeMeterModal } from '../../../common/metering';
import { performedModalRecompute, showForm, triggerModalRecompute } from '../../../actions';
import { getModalRecomputeFlag } from '../../../selectors/navigation';
import { HOME } from '../../../common/constants';

/**
 * @description Since we have to perform a few different fetches
 * when performing inline registration/sign-in (user info, meter info)
 * we want to defer recomputing the regwall state until all the different
 * fetches have completed successfully
 * @function useModalComputer
 * @param {import('@surfline/web-common').MeterState} meter
 * @param {import('@surfline/web-common').UserState} user
 * @param {import('@surfline/web-common').UserState['entitlements']} entitlements
 * @return {JSX.Element | null}
 */
export const useModalComputer = (meter, user, entitlements) => {
  const dispatch = useDispatch();
  const shouldRecompute = useSelector(getModalRecomputeFlag);
  const pathname = useSelector((state) => state?.router?.location?.pathname);

  const [modalToRender, setModalToRender] = useState(computeMeterModal(meter, user, entitlements));

  const recomputeModal = useCallback(
    () => setModalToRender(computeMeterModal(meter, user, entitlements)),
    [meter, user, entitlements],
  );

  // If the route changes, we also want to re-compute the modal since
  // the `meterRemaining` may have changed
  useEffect(() => {
    if (pathname) {
      dispatch(triggerModalRecompute());
    }
  }, [pathname, dispatch]);

  useEffect(() => {
    if (shouldRecompute) {
      recomputeModal();
      dispatch(performedModalRecompute());
      dispatch(showForm(HOME));
    }
  }, [shouldRecompute, dispatch, recomputeModal]);

  return modalToRender;
};
